import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Calendar, Clock, CheckCircle2, ArrowRight, Phone, Mail, MessageCircle, X } from "lucide-react";

const _fmtWhen = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

const _hoursLeft = (iso) => {
  if (!iso) return null;
  try {
    const diffMs = new Date(iso).getTime() - Date.now();
    if (diffMs <= 0) return "overdue";
    const h = Math.floor(diffMs / 3600000);
    const m = Math.floor((diffMs % 3600000) / 60000);
    return h >= 1 ? `${h}h ${m}m left` : `${m}m left`;
  } catch { return null; }
};

const _telHref = (phone) => (phone ? `tel:${String(phone).replace(/\s+/g, "")}` : null);
const _waHref = (phone) => {
  if (!phone) return null;
  const digits = String(phone).replace(/[^\d]/g, "");
  return digits ? `https://wa.me/${digits}` : null;
};

/**
 * Admin-facing list of the next N upcoming sessions (scheduled + pending_acceptance).
 * Reuses the /admin/overview endpoint's `upcoming_sessions` payload so contact info
 * is already embedded. Each row shows a clear status badge + a Details button that
 * opens a contact modal (call / WhatsApp / email links) so the admin can reach both
 * parties to confirm attendance.
 */
export default function AdminUpcomingList({ limit }) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);
  const [detailsBooking, setDetailsBooking] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get("/admin/overview")
      .then((r) => { if (!cancelled) setRows(r.data?.upcoming_sessions || []); })
      .catch(() => { if (!cancelled) setErr("Could not load upcoming sessions."); });
    return () => { cancelled = true; };
  }, []);

  if (err) return <div className="brand-card p-6 text-[#7A7D96] text-sm" data-testid="admin-upcoming-error">{err}</div>;
  if (rows === null) return <div className="brand-card p-6 animate-pulse h-40" />;

  const visible = typeof limit === "number" ? rows.slice(0, limit) : rows;
  return (
    <section className="brand-card p-6 mb-8" data-testid="admin-upcoming-list">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-extrabold text-[#3D405B]">Upcoming sessions</h2>
          <p className="text-xs text-[#7A7D96] mt-1">Confirmed and awaiting-acceptance bookings. Tap <span className="font-bold text-primary">Details</span> to reach the client or counsellor.</p>
        </div>
        <Calendar size={18} className="text-primary" />
      </div>
      <ul className="divide-y divide-[#3D405B]/5">
        {visible.length === 0 && (
          <li className="text-sm text-[#7A7D96] py-3">No sessions in the queue.</li>
        )}
        {visible.map((s) => {
          const isPending = s.status === "pending_acceptance";
          return (
            <li key={s.booking_id} className="py-3 flex items-center gap-3 flex-wrap" data-testid={`admin-upcoming-row-${s.booking_id}`}>
              <StatusBadge pending={isPending} />
              <div className="flex-1 min-w-[180px]">
                <div className="text-sm font-extrabold text-[#3D405B]">
                  {s.client?.name || "—"} <span className="text-[#7A7D96] font-normal">→</span> {s.counsellor?.name || "—"}
                </div>
                <div className="text-xs text-[#7A7D96] mt-0.5 flex items-center gap-2 flex-wrap">
                  <span>{_fmtWhen(s.scheduled_at)}</span>
                  {s.session_mode && <span className="pill bg-muted/50 text-[#3D405B] text-[10px]">{s.session_mode === "in_person" ? "In-person" : "Online"}</span>}
                  {isPending && s.acceptance_deadline && (
                    <span className="inline-flex items-center gap-1 text-[11px] font-bold text-[#B84A3E]">
                      <Clock size={11} /> {_hoursLeft(s.acceptance_deadline)}
                    </span>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setDetailsBooking(s)}
                className="text-xs font-extrabold text-primary hover:underline inline-flex items-center gap-1"
                data-testid={`admin-upcoming-details-${s.booking_id}`}
              >
                Details <ArrowRight size={12} />
              </button>
            </li>
          );
        })}
      </ul>
      {detailsBooking && (
        <ContactDetailsModal booking={detailsBooking} onClose={() => setDetailsBooking(null)} />
      )}
    </section>
  );
}

function StatusBadge({ pending }) {
  if (pending) {
    return (
      <span
        className="pill inline-flex items-center gap-1 bg-[#FFF3E4] text-[#B84A3E] border border-[#E07A5F]/30"
        data-testid="badge-awaiting"
      >
        <Clock size={11} /> Awaiting counsellor
      </span>
    );
  }
  return (
    <span
      className="pill inline-flex items-center gap-1 bg-secondary/15 text-secondary border border-secondary/30"
      data-testid="badge-accepted"
    >
      <CheckCircle2 size={11} /> Accepted
    </span>
  );
}

function ContactRow({ icon: Icon, label, value, href, testid }) {
  if (!value) return <div className="text-xs text-[#7A7D96]">{label}: —</div>;
  if (href) {
    return (
      <a
        href={href}
        target={href.startsWith("http") ? "_blank" : undefined}
        rel="noreferrer"
        className="inline-flex items-center gap-2 text-sm text-primary font-bold hover:underline"
        data-testid={testid}
      >
        <Icon size={14} /> {value}
      </a>
    );
  }
  return (
    <div className="inline-flex items-center gap-2 text-sm text-[#3D405B]">
      <Icon size={14} className="text-[#7A7D96]" /> {value}
    </div>
  );
}

function PartyCard({ role, person, testid }) {
  return (
    <div className="rounded-2xl border border-[#3D405B]/10 p-4 bg-white" data-testid={testid}>
      <div className="text-[10px] uppercase font-extrabold tracking-[0.16em] text-[#7A7D96]">{role}</div>
      <div className="text-base font-extrabold text-[#3D405B] mt-1">{person?.name || "—"}</div>
      <div className="mt-3 grid gap-2">
        <ContactRow icon={Phone} label="Phone" value={person?.phone} href={_telHref(person?.phone)} testid={`${testid}-call`} />
        <ContactRow icon={MessageCircle} label="WhatsApp" value={person?.phone ? "WhatsApp" : ""} href={_waHref(person?.phone)} testid={`${testid}-wa`} />
        <ContactRow icon={Mail} label="Email" value={person?.email} href={person?.email ? `mailto:${person.email}` : null} testid={`${testid}-email`} />
      </div>
    </div>
  );
}

function ContactDetailsModal({ booking, onClose }) {
  const isPending = booking.status === "pending_acceptance";
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
      data-testid="admin-upcoming-details-modal"
    >
      <div
        className="brand-card p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h3 className="text-xl font-extrabold text-[#3D405B]">Confirm & follow up</h3>
            <p className="text-xs text-[#7A7D96] mt-1">Reach out to both parties to confirm attendance for the upcoming session.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-[#7A7D96] hover:text-[#3D405B]"
            data-testid="admin-upcoming-details-close"
          >
            <X size={20} />
          </button>
        </div>

        <div className="rounded-2xl bg-muted/30 p-4 mb-4 flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="text-[10px] uppercase font-extrabold tracking-[0.16em] text-[#7A7D96]">Session</div>
            <div className="text-sm font-extrabold text-[#3D405B] mt-1">{_fmtWhen(booking.scheduled_at)}</div>
            {booking.topic && <div className="text-xs text-[#7A7D96] mt-1">Topic: {booking.topic}</div>}
          </div>
          <StatusBadge pending={isPending} />
        </div>

        {isPending && booking.acceptance_deadline && (
          <div className="rounded-2xl bg-[#FFF3E4] border border-[#E07A5F]/30 p-3 mb-4 text-xs text-[#B84A3E] font-bold">
            <Clock size={12} className="inline mr-1" /> Counsellor has {_hoursLeft(booking.acceptance_deadline)} to accept.
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <PartyCard role="Client" person={booking.client} testid="modal-client" />
          <PartyCard role="Counsellor" person={booking.counsellor} testid="modal-counsellor" />
        </div>

        <div className="mt-4 text-[11px] text-[#7A7D96]">
          Use the phone, WhatsApp or email links above to reach either party and confirm they'll attend.
        </div>
      </div>
    </div>
  );
}
